import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { settingsService } from '../services/api';
import toast from 'react-hot-toast';
import { Clock, Save, RefreshCw } from 'lucide-react';

const PresentationSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    presentation_duration_minutes: 60,
    presentation_buffer_minutes: 30
  });

  useEffect(() => {
    loadSettings();
  }, []);

  // Verificar se é GERENCIA
  if (user?.role !== 'GERENCIA') {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h2 className="text-lg font-medium text-red-800">Acesso Negado</h2>
        <p className="text-red-600">Apenas GERENCIA pode acessar esta página.</p>
      </div>
    );
  }

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsService.getPresentationSettings();
      setSettings({
        presentation_duration_minutes: parseInt(response.data.presentation_duration_minutes) || 60,
        presentation_buffer_minutes: parseInt(response.data.presentation_buffer_minutes) || 30
      });
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsService.updatePresentationSettings(settings);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-6 h-6" />
            Configurações de Apresentação
          </h1>
          <p className="text-gray-600 mt-1">
            Configure os parâmetros padrão para agendamento de apresentações
          </p>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Carregando configurações...</span>
            </div>
          ) : (
            <>
              {/* Duração da Apresentação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duração da Apresentação (minutos)
                </label>
                <input
                  type="number"
                  min="15"
                  max="180"
                  value={settings.presentation_duration_minutes}
                  onChange={(e) => setSettings({
                    ...settings,
                    presentation_duration_minutes: parseInt(e.target.value) || 60
                  })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tempo estimado para cada apresentação (15-180 minutos)
                </p>
              </div>

              {/* Buffer entre Apresentações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Intervalo entre Apresentações (minutos)
                </label>
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={settings.presentation_buffer_minutes}
                  onChange={(e) => setSettings({
                    ...settings,
                    presentation_buffer_minutes: parseInt(e.target.value) || 0
                  })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tempo de buffer entre uma apresentação e outra para deslocamento (0-60 minutos)
                </p>
              </div>

              {/* Resumo */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-medium text-blue-800 mb-2">Resumo da Configuração</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Duração: <strong>{settings.presentation_duration_minutes} minutos</strong></li>
                  <li>• Buffer: <strong>{settings.presentation_buffer_minutes} minutos</strong></li>
                  <li>• Tempo total por apresentação: <strong>{settings.presentation_duration_minutes + settings.presentation_buffer_minutes} minutos</strong></li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={loadSettings}
            disabled={loading || saving}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={loading || saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Salvar Configurações
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PresentationSettings;
